import orderBy from 'lodash/orderBy'
import { observer } from 'mobx-react'
import { useMemo } from 'react'

import { CheckboxBasedFilter, OptionsProps } from '@avantstay/backoffice-core'

import { casesFilterOptionsStore } from '@new/domains/operations/screens/CasesV2/stores/CasesFilterOptionsStore'
import { casesFiltersStore } from '@new/domains/operations/screens/CasesV2/stores/CasesFiltersStore'
import { casesStore } from '@new/domains/operations/screens/CasesV2/stores/CasesStore'
import { CaseFilterIds } from '@new/domains/operations/screens/CasesV2/utils/config'
import {
  buildTaxonomyCategoryOptions,
  oldTaxonomyGroup,
  TaxonomyFilterLevel,
} from '@new/domains/operations/screens/CasesV2/utils/taxonomyFilterOptions'
import { parseLabel } from '@new/domains/operations/utils/parseLabel'

import * as S from './commonFilterStyles.style'
import { useTaxonomyColumnFilter } from './useTaxonomyColumnFilter'

const _CategoriesFilter = () => {
  const { caseInputs, isLoadingCaseInputs } = casesStore
  const { taxonomyTasks, isLoadingTaxonomyTasks } = casesFilterOptionsStore
  const {
    recordTypes,
    taxonomyRecordTypes,
    taxonomyTypes,
    categories,
    taxonomyCategories,
    shouldShowLegacyFilterOptions,
    shouldShowTaxonomyFilterOptions,
  } = casesFiltersStore

  const taxonomyOptions = useMemo(
    () =>
      shouldShowTaxonomyFilterOptions
        ? orderBy(
            buildTaxonomyCategoryOptions(taxonomyTasks, {
              taskNodeIds: taxonomyRecordTypes,
              serviceTypeNodeIds: taxonomyTypes,
            }),
            ['group', 'label'],
          )
        : [],
    [taxonomyTasks, taxonomyRecordTypes, taxonomyTypes, shouldShowTaxonomyFilterOptions],
  )

  const options = useMemo(() => {
    if (!caseInputs || !shouldShowLegacyFilterOptions) return taxonomyOptions
    const legacyOptions = Object.entries(caseInputs).reduce<OptionsProps<string>[]>(
      (acc, [recordType, caseInput]) => {
        if (!recordTypes || recordTypes.includes(recordType)) {
          const groupName = oldTaxonomyGroup(parseLabel(recordType))
          const listByRecordType: OptionsProps<string>[] = []

          caseInput.caseTypes?.forEach(type => {
            type.categories?.forEach(categoryObj => {
              listByRecordType.push({
                label: parseLabel(categoryObj.category.label),
                value: categoryObj.category.apiName,
                group: groupName,
                hasFullWidth: true,
              })
            })
          })
          return [...acc, ...listByRecordType]
        }
        return acc
      },
      [],
    )

    return [...taxonomyOptions, ...orderBy(legacyOptions, ['group', 'label'])]
  }, [caseInputs, recordTypes, taxonomyOptions, shouldShowLegacyFilterOptions])

  const { initialValue, handleDataFilter } = useTaxonomyColumnFilter({
    legacyFilterId: CaseFilterIds.Category,
    level: TaxonomyFilterLevel.Categories,
    legacyValues: categories,
    taxonomyValues: taxonomyCategories,
    taxonomyOptions,
  })

  return (
    <S.CheckBoxWrapper optionsLength={options.length}>
      <CheckboxBasedFilter
        shouldGroupBy
        isLoadingOptions={isLoadingCaseInputs || isLoadingTaxonomyTasks}
        title="Categories"
        options={options}
        initialValue={initialValue}
        shouldUpdateOnInitialValueChange
        onChange={handleDataFilter}
      />
    </S.CheckBoxWrapper>
  )
}

export const CategoriesFilter = observer(_CategoriesFilter)
